import React, { memo, useCallback, useEffect, useMemo, useState } from "react"
import {
    CardContent, CircularProgress,
    Collapse,
    Grid,
    makeStyles,
    Typography
} from "@material-ui/core"
import Card from "shared/Components/Card/Card"
import { Localized, ReactLocalization, useLocalization } from "@fluent/react"
import {
    DeliveryPayloadType,
    IBasketMeetingPlanningFormField,
    IEditMeetingOrderData
} from "shared/Types/MeetingPlanning/deliveryForm"
import { useFormikContext } from "formik"
import { LocalizedStrict } from "shared/Modules/Localization/Components/Localized"
import { FormikDatePickerControlled } from "shared/Components/Form/Formik"
import TimePickerControl from "shared/Components/Form/TimePickerControl"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronDown } from "@fortawesome/pro-light-svg-icons/faChevronDown"
import { faChevronUp } from "@fortawesome/pro-light-svg-icons/faChevronUp"
import { FormInputField, InputType, ValidationRule, ValidationType } from "shared/Modules/Forms/formTypes"
import { Alert, AlertTitle } from "@material-ui/lab"
import { useQuery } from "shared/Modules/Query/useQuery"
import { useToken } from "shared/Modules/Login/useToken"
import { useSelector } from "react-redux"
import { selectUserCanteenId } from "shared/Modules/User/userSelectors"
import { isEmpty } from "lodash-es"
import { getDeadlineMessage } from "../../../../mobile/Api/GetDeadlineMessage"
import { ILooseGroup, IMeetingServiceConfig } from "../../../Types/MeetingPlanning/meetingServiceConfiguration"
import { dynamicFieldToInputField } from "../../Forms/formLib"
import { IConfigField } from "../../Forms/dynamicFormFieldsTypes"
import { DynamicFormField } from "../../Forms/Components/DynamicFormField"

const useDeliveryFormStyles = makeStyles((theme) => ({
    mr_2: {
        marginRight: theme.spacing(2),
    },
    mt_2: {
        marginTop: theme.spacing(2),
    },
    mb_2: {
        marginBottom: theme.spacing(2),
    },
    mb_3: {
        marginBottom: theme.spacing(3),
    },
    card: {
        marginTop: '16px !important',
    },
    cardContent: {
        padding: '16px !important'
    },
    collapse: {
        width: '100%',
        minHeight: '30px !important',
        visibility: 'visible',
    },
    title: {
        cursor: 'pointer',
        marginBottom: theme.spacing(4),

        '& *': {
            cursor: 'pointer',
        }
    },
    alertWrap: {
        color: "#0369A1",
        background: "#F0F9FF",
        borderRadius: ".5rem",
        marginBottom: theme.spacing(2),

        "& .MuiAlert-icon": {
            color: "#0369A1",
        },
    },
    link: {
        textDecoration: "underline",
        width: "100%",
        textAlign: "center",
        fontWeight: 500,
        cursor: "pointer"
    }
}))

export function useDeliveryFormFields(initialMeetingPlanningData: IEditMeetingOrderData | null, meetingServiceConfigData: IMeetingServiceConfig | null, requiredMessage: string, l10n: ReactLocalization): FormInputField[] {
    const dynamicMeetingDetailsFields = meetingServiceConfigData?.sections?.find((section) => section.title === 'Meeting details')?.groups?.find((group) => !group.title)?.fields || []
    const dynamicDeliveryFields = meetingServiceConfigData?.sections?.find((section) => section.title === 'Delivery')?.groups?.find((group) => group)?.fields || []
    const dynamicGroupFields: IConfigField[] = meetingServiceConfigData?.groups?.length
        ? meetingServiceConfigData.groups.reduce((acc: IConfigField[], curr: ILooseGroup) => {
            const orderNote = curr?.fields?.find((field) => field.key === 'MERCHANT_ORDER_NOTE');
            if (orderNote) {
                acc.push(orderNote);
            }
            return acc;
        }, [])
        : [];

    const dynamicFields = [...dynamicMeetingDetailsFields, ...dynamicDeliveryFields, ...dynamicGroupFields]

    const fields = dynamicFields?.length ? dynamicFields.map((field) => dynamicFieldToInputField(field, initialMeetingPlanningData, requiredMessage, l10n)).filter((field) => field.key !== 'MEETING_RESPONSIBLE') : []
    const filteredFields = fields?.length ? fields.filter((field) => field.valueType) : []

    return useMemo<FormInputField[]>(() => filteredFields ? [
        ...filteredFields,
        {
            name: "deliveryDate",
            type: InputType.TEXT,
            initialValue: initialMeetingPlanningData?.orderData?.deliveryDate
                ? initialMeetingPlanningData.orderData.deliveryDate
                : "",
            disabled: !!initialMeetingPlanningData?.orderData?.deliveryDate,
            label: l10n.getString("delivery-form-meeting-field-date-label"),
            validations: [
                {
                    type: ValidationType.REQUIRED,
                    message: l10n.getString("delivery-form-meeting-field-date-required", undefined, "Leveringsdato er påkrævet")
                }
            ]
        },
        {
            name: "deliveryTime",
            type: InputType.TEXT,
            initialValue: initialMeetingPlanningData?.orderData?.deliveryTime || "",
            label: l10n.getString("delivery-form-delivery-time-field-label"),
            validations: [
                {
                    type: ValidationType.REQUIRED,
                    message: l10n.getString("delivery-form-meeting-field-delivery-time-required", undefined, "Leveringstidspunkt er påkrævet")
                }
            ]
        },
    ] : [], [l10n, meetingServiceConfigData])
}

const DeliveryControls = ({classes, initialMeetingPlanningData, meetingServiceConfigData}: { classes: ReturnType<typeof useDeliveryFormStyles>, initialMeetingPlanningData: IEditMeetingOrderData | null, meetingServiceConfigData: IMeetingServiceConfig | null }) => {
    const token = useToken()
    const userCanteenId = useSelector(selectUserCanteenId)

    const { values, setFieldValue } = useFormikContext<IBasketMeetingPlanningFormField>()
    const [loading, setLoading] = useState(false)

    const { response: deadlineMessage } = useQuery(
        () => {
            setLoading(true)
            return getDeadlineMessage(values.deliveryDate, userCanteenId, token).finally(() => setLoading(false))
        },
        [token, values?.deliveryDate],
        Boolean(token && userCanteenId && values?.deliveryDate)
    )


    const deadLineResponseData = useMemo(() => {
        if (!deadlineMessage.loading &&
            !deadlineMessage.failed &&
            deadlineMessage.response &&
            !isEmpty(deadlineMessage.response.data)) {
            return deadlineMessage.response.data
        }

        return null
    }, [deadlineMessage])

    useEffect(() => {
        if (
            deadLineResponseData
        ) {
            setFieldValue("isConfirmed", deadLineResponseData.deadlineMesssage.isConfirmed || false)
            setFieldValue("deadlineMessage", deadLineResponseData.deadlineMesssage.message?.displayRule || "")
            setFieldValue("deadlineMerchantMessage", deadLineResponseData.deadlineMesssage.message?.merchantMessage || "")
            return
        }

        setFieldValue("isConfirmed", false)
        setFieldValue("deadlineMessage", "")
        setFieldValue("deadlineMerchantMessage", "")
    }, [deadlineMessage])

    return <>
        <Grid item className={classes.mb_3}>
            <LocalizedStrict id="delivery-form-meeting-field-date-label" attrs={{ label: true, placeholder: true }}>
                <FormikDatePickerControlled
                    required
                    disablePast
                    disabled={!!initialMeetingPlanningData?.orderData?.deliveryDate}
                    closingDates={meetingServiceConfigData?.closingDates || []}
                    allowedWeekdays={meetingServiceConfigData?.openingHours || []}
                    name="deliveryDate"
                    label="Leveringsdato"
                    placeholder="Vælg Leveringsdato"
                />
            </LocalizedStrict>
        </Grid>
        {deadLineResponseData?.deadlineMesssage?.message?.displayRule && (
                <Alert severity="info" className={classes.alertWrap}>
                    <AlertTitle>
                        <Typography variant="caption">{deadLineResponseData.deadlineMesssage.message.displayRule}</Typography>
                    </AlertTitle>
                </Alert>
            )}
        {loading
            ? <Grid container item alignItems="center" justifyContent="center" className={classes.mb_3}>
                <CircularProgress variant="indeterminate" />
            </Grid>
            : null}
        <Grid item className={classes.mb_3}>
            <LocalizedStrict id="delivery-form-delivery-time-field-label" attrs={{ label: true, placeholder: true }}>
                <TimePickerControl
                    required
                    name="deliveryTime"
                    dateFieldName="deliveryDate"
                    openingHours={meetingServiceConfigData?.openingHours || []}
                    label="Vælg leveringstid"
                    placeholder="Vælg Leveringstid"
                />
            </LocalizedStrict>
        </Grid>
    </>
}

const MeetingPlanningDeliveryForm = ({ initialMeetingPlanningData, meetingServiceConfigData }: { initialMeetingPlanningData: IEditMeetingOrderData | null, meetingServiceConfigData: IMeetingServiceConfig | null }) => {
    const classes = useDeliveryFormStyles()
    const { l10n } = useLocalization()

    const requiredMessage = l10n.getString('error-required', undefined, 'Dette felt skal udfyldes')

    const [isCollapsed, setIsCollapsed] = useState(true)
    const [deliveryType, setDeliveryType] = useState<DeliveryPayloadType | null>(null)

    const toggleCollapse = useCallback(() => {
        setIsCollapsed(prev => !prev)
    }, [setIsCollapsed])

    const deliveryFormFields = useDeliveryFormFields(initialMeetingPlanningData, meetingServiceConfigData, requiredMessage, l10n)

    const fields = useMemo(() => {
        return deliveryType === DeliveryPayloadType.DELIVERY
            ? deliveryFormFields.map((field) => {
                if (field.key === 'DELIVERY_LOCATIONS') {
                  return {
                      ...field,
                      isActive: true,
                      required: true,
                      validations: [{
                          type: ValidationType.IS_EMPTY_LIST,
                          message: requiredMessage
                      } as ValidationRule]
                  }
                }

                if (field.key === 'OTHER_DELIVERY_LOCATION') {
                    return {
                        ...field,
                        isActive: true,
                    }
                }

                return field
            })
            : deliveryFormFields;
    }, [deliveryType, deliveryFormFields])

    const handleDeliveryChange = (type: DeliveryPayloadType) => {
        setDeliveryType(type)
    }

    return <Card className={classes.card}>
        <CardContent className={classes.cardContent}>
            <Collapse in={isCollapsed} className={classes.collapse}>
                <Grid item container direction="column" wrap="nowrap" justifyContent="flex-start">
                    <Grid item container alignItems="center" justifyContent="space-between" className={classes.title} onClick={toggleCollapse}>
                        <Localized id="delivery-type-option-delivery">
                            <Typography variant="h6">Levering</Typography>
                        </Localized>
                        <FontAwesomeIcon icon={isCollapsed ? faChevronUp : faChevronDown}/>
                    </Grid>
                    <Grid item>
                        <DeliveryControls classes={classes} initialMeetingPlanningData={initialMeetingPlanningData} meetingServiceConfigData={meetingServiceConfigData} />
                        {fields.map((field, i) => (
                            <Grid key={field.name} item className={classes.mb_3}>
                                <DynamicFormField field={field} onDeliveryChange={handleDeliveryChange} />
                            </Grid>
                        ))}
                    </Grid>
                </Grid>
            </Collapse>
        </CardContent>
    </Card>
}

export default memo(MeetingPlanningDeliveryForm)
